import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import { REVIEWS_PAGE_PATH } from 'dpl/shared/constants/urls';
import Divider from 'dpl/components/BreederProfile/mobile/components/shared/Divider';
import SmartLink from 'dpl/components/SmartLink';
import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';

import ownerStories1 from 'dpl/assets/homepage/owner_stories_1.png';
import ownerStories2 from 'dpl/assets/homepage/owner_stories_2.png';
import ownerStories3 from 'dpl/assets/homepage/owner_stories_3.png';

const ownerStories = [
  {
    id: 51971,
    display_name: 'Christine B.',
    testimonial:
      'We couldn’t be happier with our experience working with Margaret at Golden Bundles. From the very beginning, she was kind, responsive, and truly passionate about her dogs. You can tell she puts her heart into raising these puppies, they’re healthy, well-socialized, and so full of love.',
    image_url: ownerStories1,
    program_name: 'Sporting Ridge K9’s',
    breeder_profile_url:
      'https://www.gooddog.com/breeders/sporting-ridge-k9s-indiana#tab=stories'
  },
  {
    id: 51975,
    display_name: 'Alm H.',
    testimonial:
      'Angela at Turkey Creek K9 was so great through the whole process. She made me feel like I knew what I was doing. I’m a first time dog owner and she gave me the right advice. The process of adoption from her was flawless! My family and I absolutely love our puppy! Our little Boone is well adapted to our family now and is loving life.',
    image_url: ownerStories2,
    program_name: 'TurkeyCreek-K9',
    breeder_profile_url:
      'https://www.gooddog.com/breeders/turkeycreekk9-indiana#tab=stories'
  },
  {
    id: 51975,
    display_name: 'Michael G.',
    testimonial:
      'Lynnell was great and super easy to work with! She responded promptly and answered all our questions. This was our first time purchasing a dog from a breeder and I am completely satisfied and happy with our choice! The total process was also super easy using gooddog.com. I would do it again the exact same way and I would recommend Lynnell’s puppies to my friends.',
    image_url: ownerStories3,
    program_name: 'PuppyPaws4U',
    breeder_profile_url:
      'https://www.gooddog.com/breeders/puppypaws4u-california#tab=stories'
  }
];

const OTHER_STORIES_BUTTON_CTA = 'Read other stories';

export default function OwnerStoriesSection() {
  const splideRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const isNextButtonDisabled = activeIndex >= ownerStories.length - 1;
  const isPrevButtonDisabled = activeIndex <= 0;

  function handleActiveSlide(slide) {
    setActiveIndex(slide.index);
  }

  function handleOtherStoriesClick() {
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      view: ANALYTICS_VIEWS.HOME,
      view_feature: ANALYTICS_VIEW_FEATURES.TESTIMONIALS_STRIP,
      button_cta: OTHER_STORIES_BUTTON_CTA
    });
  }

  function handleArrowClick(direction) {
    splideRef.current.go(direction);
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      view: ANALYTICS_VIEWS.HOME,
      view_feature: ANALYTICS_VIEW_FEATURES.TESTIMONIALS_STRIP,
      button_cta: direction
    });
  }

  return (
    <section className="OwnerStoriesSection bg-sky section-vertical-padding">
      <div className="container-xl section-fade">
        <h3 className="font-28 md:font-40 mb4 lh-title tc-ns">
          Stories from Good Dog families
        </h3>
        <p className="secondary font-16 mb10 mb16-md tc-ns">
          Hear how families found their dog safely and confidently.
        </p>
        <div className="OwnerStoriesCarousel">
          <Splide
            ref={splideRef}
            onActive={handleActiveSlide}
            options={{
              arrows: false,
              easing: 'cubic-bezier(0.5, 0, 0.5, 1)',
              type: 'fade',
              pagination: false,
              start: activeIndex
            }}
          >
            {ownerStories.map((story, idx) => (
              <SplideSlide key={idx}>
                <div className="flex flex-column flex-lg-row items-center gap-24 md:gap-32 bg-stone-700 white br12 br4-ns pt2 pb4 pb2-md pl2 pr2 pr8-md">
                  <div
                    className="flex-none"
                    style={{ maxWidth: '349px', maxHeight: '349px' }}
                  >
                    <SmartImage
                      src={story.image_url}
                      alt={story.program_name}
                    />
                  </div>
                  <div className="ph2 ph0-lg">
                    <p className="font-serif font-18 lg:font-20 xl:font-24">
                      “{story.testimonial}”
                    </p>
                    <Divider
                      containerClassName="o-20 pt6 pb4"
                      className="mv0 b--sky"
                    />
                    <div className="font-14 md:font-16 flex flex-column flex-lg-row justify-between content-end">
                      <div className="o-70">
                        <p>{story.display_name}</p>
                        <p>
                          Worked with{' '}
                          <a
                            className="underline"
                            href={story.breeder_profile_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {story.program_name}
                          </a>
                          , Good Dog breeder
                        </p>
                      </div>
                      <SmartLink
                        onClick={handleOtherStoriesClick}
                        to={REVIEWS_PAGE_PATH}
                        className="arrow-link fw-medium flex gap-8 items-center justify-between self-end-l nowrap"
                        target="_blank"
                      >
                        {OTHER_STORIES_BUTTON_CTA}
                        <Icon name="fetch-arrow-right" className="o-60" />
                      </SmartLink>
                    </div>
                  </div>
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>
        <div className="mt10 mt12-md relative w-100" style={{ height: 2 }}>
          <span className="absolute bg-stone-700 db o-30 h-100 w-100" />
          <span
            className="absolute bg-stone-700 db h-100 transition"
            style={{
              width: `${(100 / ownerStories.length) * (activeIndex + 1)}%`
            }}
          />
        </div>

        <div className="mt6 stone-700">
          <button
            type="button"
            className={classnames('bg-white-90 br-100 mr4', {
              'bg-white-40': isPrevButtonDisabled
            })}
            disabled={isPrevButtonDisabled}
            onClick={() => handleArrowClick('<')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-left-md" />
            </div>
          </button>
          <button
            type="button"
            className={classnames('bg-white-90 br-100', {
              'bg-white-40': isNextButtonDisabled
            })}
            disabled={isNextButtonDisabled}
            onClick={() => handleArrowClick('>')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-right-md" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
